<script>
  import { onMount, tick } from 'svelte';
  import { createEventDispatcher } from 'svelte';

  import BrowserUtils from '@utils/browser-utils';

  const dispatch = createEventDispatcher();

  export let type = 'text';
  export let label;
  export let name;
  export let value = '';
  export let error;
  export let placeholder = null;
  export let loading = false;
  export let inputEl = null;
  export let focus = false;
  export let focusAtFront = false; // when focusing, move the cursor to front of input
  export let rightIconPath;
  export let leftIconPath;
  export let isDisabled = false;
  export let withRightLink = false;
  export let withRightIconSlot = false;
  export let preventEnterOnSubmit = false;
  export let preventTab = false;
  export let showLoadingIconOnLeftSide = true;
  export let customErrorEvent = false;

  let previousValue = '';

  let handleKeyDown = function (event) {
    let keyCode = event.which || event.keyCode || 0;

    if (keyCode === 13) {
      if (preventEnterOnSubmit) {
        event.preventDefault();
      }
      dispatch('enter');
    }

    if (preventTab && keyCode === 9) {
      event.preventDefault();
      dispatch('tab', { event: event });
    }

    dispatch('keydown', {
      event: event,
      which: keyCode,
      value: event.target.value,
    });
  };

  let setFocus = async function (node) {
    if (focus || focusAtFront) {
      await tick();
      BrowserUtils.focus(node, focusAtFront);
    }
  };

  let handleBlur = function (event) {
    value = event.target.value;

    if (value != previousValue) {
      dispatch('change', {
        value: event.target.value,
      });
    }

    dispatch('blur', {
      event: event,
      value: event.target.value,
    });
  };

  let handleKeyUp = function (event) {
    value = event.target.value;
    let keyCode = event.which || event.keyCode || 0;

    dispatch('keyup', {
      event: event,
      which: keyCode,
      value: event.target.value,
    });

    if (value != previousValue) {
      dispatch('change', {
        value: event.target.value,
      });
    }

    previousValue = event.target.value;
  };

  onMount(() => {
    dispatch('mounted', { input: inputEl });
  });
</script>

<style style="text/scss" lang="scss">
  .input-container {
    position: relative;
    display: flex;
    justify-content: center;
    flex-direction: column;

    .input-icon-right {
      position: absolute;
      right: 20px;
      top: 14px;
      padding-left: 15px;
      z-index: 1;
      opacity: 0.75;
      height: 22.5px;

      display: flex;
      flex-direction: column;
      justify-content: center;

      img {
        cursor: pointer;
        height: 18px;
        width: 18px;
      }
    }

    .input-icon-left {
      position: absolute;
      left: 5px;
      top: 14px;
      padding-left: 15px;
      z-index: 1;
      opacity: 0.75;
      height: 22.5px;

      display: flex;
      flex-direction: column;
      justify-content: center;

      img {
        cursor: pointer;
        height: 24px;
        width: 24px;
      }

      &.rounded img {
        border-radius: 1000px;
      }
    }

    label {
      font-size: 15px;
      line-height: 15px;
      opacity: 0.5;
      font-weight: normal;
    }

    .label-container {
      margin-bottom: 12px;
    }

    input {
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;

      transition:
        border-color 0.15s ease-in-out,
        box-shadow 0.15s ease-in-out;
      border-radius: 15px;
      border: 1px solid #ececec;
      padding-left: 21px;
      padding-right: 21px;
      height: 50px;
      font-size: 14px;
      font-weight: 500;
      line-height: 17px;
      color: #495057;
      width: 100%;

      &::placeholder {
        opacity: 0.85;
        font-weight: 400;
      }

      &:focus {
        background-color: #f6f4fe;
        border-color: #a893f9;
        outline: 0;
        outline: none;
      }

      &:active,
      &:hover {
        outline: none;
      }
    }

    .input-error-msg {
      width: 100%;
      margin-top: 0.25rem;
      font-size: 80%;
      color: #ff5567;
    }

    .input-error {
      border-color: #ff5567 !important;
      padding-right: calc(40px) !important;
      background-repeat: no-repeat;
      background-position: center right calc(8px);
      background-size: calc(14px) calc(14px);
      background-color: #fff1f1 !important;
    }

    .error-icon {
      cursor: pointer;
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='%23dc3545' viewBox='-2 -2 7 7'%3e%3cpath stroke='%23dc3545' d='M0 0l3 3m0-3L0 3'/%3e%3ccircle r='.5'/%3e%3ccircle cx='3' r='.5'/%3e%3ccircle cy='3' r='.5'/%3e%3ccircle cx='3' cy='3' r='.5'/%3e%3c/svg%3E");
      width: 15px;
      height: 15px;
    }

    .loading-input {
      padding-right: 50px !important;
    }

    .has-left-icon {
      padding-left: 50px !important;
    }
    * :global(.request-verification:hover) {
      color: #7054dc !important;
    }
  }
</style>

<div class="input-container {$$props.class}">
  {#if label}
    {#if withRightLink}
      <div class="label-container {$$props.labelContainerClass}">
        <div style="display: flex; align-items: center; justify-content: space-between;">
          <label for="" class={$$props.labelClass}>
            {@html label}
          </label>
          <slot name="right-side-link" />
        </div>
        <slot name="sub-label" />
      </div>
    {:else}
      <div class="label-container {$$props.labelContainerClass}">
        <label for="" class={$$props.labelClass}>
          {@html label}
        </label>
        <slot name="sub-label" />
      </div>
    {/if}
  {/if}

  <div style="position: relative">
    {#if rightIconPath || loading}
      {#if loading}
        <div class="input-icon-right {$$props.loadingClass}">
          <i class="fa fa-spin fa-spinner {$$props.loadingSpinnerClass}" />
        </div>
      {:else}
        <div class="input-icon-right {$$props.loadingClass}">
          <img src={rightIconPath} alt="right-icon" on:click={() => dispatch('right-icon-click')} />
        </div>
      {/if}
    {:else if error}
      <div class="input-icon-right {$$props.loadingClass}">
        <i
          class="error-icon"
          on:click|preventDefault|stopPropagation={(e) => {
            if (customErrorEvent) {
              dispatch('close_icon_clicked');
            } else {
              value = '';

              dispatch('keyup', {
                event: e,
                which: 46,
                value: '',
              });
              dispatch('change', {
                value: '',
              });
            }
          }} />
      </div>
    {:else if withRightIconSlot}
      <div class="input-icon-right">
        <slot name="right-side-icon" />
      </div>
    {/if}
    {#if leftIconPath}
      {#if showLoadingIconOnLeftSide && loading}
        <div class="input-icon-left {$$props.loadingClass}">
          <i class="fa fa-spin fa-spinner {$$props.loadingSpinnerClass}" />
        </div>
      {:else}
        <div class="input-icon-left {$$props.loadingClass}">
          <img src={leftIconPath} alt="left-icon" on:click={() => dispatch('left-icon-click')} />
        </div>
      {/if}
    {/if}

    <input
      use:setFocus
      style={$$props.inputStyle}
      {...$$props.inputProps}
      bind:this={inputEl}
      class:loading-input={loading}
      class:input-error={error}
      class:has-left-icon={leftIconPath}
      on:click={(e) => {
        dispatch('click', { event: e });
      }}
      on:blur={handleBlur}
      on:focus={(e) => {
        dispatch('focus', { event: e });
      }}
      on:paste={(e) => {
        dispatch('paste', { event: e });
      }}
      on:keydown={handleKeyDown}
      on:keyup={handleKeyUp}
      class={$$props.inputClass}
      type={type}
      name={name}
      placeholder={placeholder}
      disabled={isDisabled}
      value={value} />
  </div>
  {#if error}
    <div class="input-error-msg {$$props.errorClass}">
      {error}
    </div>
  {/if}
</div>
